import {
  AnyRoute,
  LazyRoute,
  Register as RouterRegister,
  RouteById,
  RouteOptions,
} from "@vixi/router";
import { ReactElement } from "react";
import { RouteOptionsById, type RouterIds } from "@vixi/router";
import { IndexPageOptions } from ".";
import { AppRouteOptions } from "./_app/route";
import { DashboardIndexPageOptions } from "./_app/dashboard";
import { CoursesIndexPageOptions } from "./_app/courses";
import { CourseIdIndexPageOptions } from "./_app/courses/$courseId";
import { SeeAnalyticsPageOptions } from "./_app/courses/$courseId/see-analytics";
import { EditAvatarsPageOptions } from "./_app/courses/$courseId/edit-avatars";
import { EditLessonsPageOptions } from "./_app/courses/$courseId/edit-lessons";
import { EditCourseInfoPageOptions } from "./_app/courses/$courseId/edit-info";
import { ManageCourseAccessPageOptions } from "./_app/courses/$courseId/manage-access";
import { FeedbackIndexPageOptions } from "./_app/feedback";
import { LoginPageOptions } from "./_auth/login";
import { AuthRouteOptions } from "./_auth/route";
import { CreateCourseIndexPageOptions } from "./_app/create";
import { CreateEbookCourseIndexPageOptions } from "./_app/create/ebook";
import { CreateLinkedinCourseFlowPageOptions } from "./_app/create/linkedin/flow";
import { AvatarIndexPageOptions } from "./_app/avatars";
import { MarketingKitIndexPageOptions } from "./_app/marketing-kit";
import { CreateAvatarPageOptions } from "./_app/avatars/create";
import { CreateEbookCourseFlowPageOptions } from "./_app/create/ebook/flow";
import { CreateLinkedinCourseIndexPageOptions } from "./_app/create/linkedin";
import { ChatIndexPageOptions } from "./_app/chat";
import { SettingsRouteOptions } from "./_app/settings/route";
import { AccountSettingsPageOptions } from "./_app/settings/account-settings";
import { SubscriptionSettingsPageOptions } from "./_app/settings/subscription";
import { PremiumRouteOptions } from "./_app/premium/route";
import { PremiumIndexPageOptions } from "./_app/premium";
import { PlansPageOptions } from "./_app/premium/plans";
import { ConfirmEmailPageOptions } from "./_auth/confirm-email";
import { SignUpPageOptions } from "./_auth/signup";
import { RootComponent } from "@vixi/ui";

export type BaseRouteOptions = Pick<
  RouteOptions<RouterRegister>,
  "loader" | "beforeLoad" | "loaderDeps" | "search" | "validateSearch"
>;
export type LazyRouteIds = Extract<RouterIds, "/admin" | `/admin${string}`>;

// Lazy RouteMap
export type LazyRouteFn<TRoutePath extends RouterIds> = () => Promise<
  LazyRoute<RouteById<AnyRoute, TRoutePath>>
>;
export type LazyRouterMapValue<TRoutePath extends RouterIds> = BaseRouteOptions & {
  component?: LazyRouteFn<TRoutePath>;
};

// Generic to enforce that the route returned matches the route path
export type SyncRouterIds = Exclude<RouterIds, LazyRouteIds>;
export type RouterMapValue = BaseRouteOptions & {
  component?: () => ReactElement;
};

type RouterMap = {
  [K in SyncRouterIds]: RouteOptionsById<K> | RouterMapValue;
};

export const routerMap: Partial<RouterMap> = {
  __root__: {
    component: RootComponent,
  },
  "/": IndexPageOptions,
  "/_auth": AuthRouteOptions,
  "/_auth/login": LoginPageOptions,
  "/_auth/signup": SignUpPageOptions,
  "/_auth/confirm-email": ConfirmEmailPageOptions,
  "/_app": AppRouteOptions,
  "/_app/dashboard/": DashboardIndexPageOptions,
  "/_app/avatars/": AvatarIndexPageOptions,
  "/_app/courses/": CoursesIndexPageOptions,
  "/_app/courses/$courseId/": CourseIdIndexPageOptions,
  "/_app/courses/$courseId/see-analytics": SeeAnalyticsPageOptions,
  "/_app/courses/$courseId/edit-avatars": EditAvatarsPageOptions,
  "/_app/courses/$courseId/edit-lessons": EditLessonsPageOptions,
  "/_app/courses/$courseId/edit-info": EditCourseInfoPageOptions,
  "/_app/courses/$courseId/manage-access": ManageCourseAccessPageOptions,
  "/_app/feedback/": FeedbackIndexPageOptions,
  "/_app/create/": CreateCourseIndexPageOptions,
  "/_app/create/ebook/": CreateEbookCourseIndexPageOptions,
  "/_app/create/ebook/flow": CreateEbookCourseFlowPageOptions,
  "/_app/create/linkedin/": CreateLinkedinCourseIndexPageOptions,
  "/_app/create/linkedin/flow": CreateLinkedinCourseFlowPageOptions,
  "/_app/marketing-kit/": MarketingKitIndexPageOptions,
  "/_app/avatars/create": CreateAvatarPageOptions,
  "/_app/chat/": ChatIndexPageOptions,
  "/_app/settings": SettingsRouteOptions,
  "/_app/settings/account-settings": AccountSettingsPageOptions,
  "/_app/settings/subscription": SubscriptionSettingsPageOptions,
  "/_app/premium": PremiumRouteOptions,
  "/_app/premium/": PremiumIndexPageOptions,
  "/_app/premium/plans": PlansPageOptions,
};
